import { useMemo, useState } from "react"
import ReactDOM from "react-dom"
import { useDispatch, useSelector } from "react-redux"

import { createLienFromDocument, parseLienDocument } from "../../../../lib/fetchApi"
import { validateBoxUploadFile } from "../../../../lib/docs"
import { useUserTabEditPermissionDisabled } from "../../../../lib/usersPermissions"
import { notificationActions } from "../../../../store/notifications"
import Button from "../../../common/Button"
import Backdrop from "../../../common/modal/Backdrop"
import Modal from "../../../common/Modal"
import ModalInnerWrapper from "../../../common/ModalInnerWrapper"
import PannelBody from "../../../fragments/PannelBody"
import PannelFooter from "../../../fragments/PannelFooter"

import classes from "./CreateLienFromDocument.module.css"

const TYPE_LABELS = {
    lien: "Lien",
    amend: "Amended",
    satisfy: "Satisfaction",
}

function errorText(response, fallback) {
    const raw = response?.errorMessage
    if (typeof raw === "string" && raw.trim()) {
        return raw
    }
    if (raw && typeof raw === "object") {
        if (typeof raw.errorMessage === "string" && raw.errorMessage.trim()) {
            return raw.errorMessage
        }
        if (typeof raw.message === "string" && raw.message.trim()) {
            return raw.message
        }
    }
    return fallback
}

function countiesMatch(practice) {
    const patientCountyId = Number(practice?.patient_county_id || 0)
    const practiceCountyId = Number(practice?.practice_county_id || 0)
    return patientCountyId > 0 && patientCountyId === practiceCountyId
}

function suggestOriginalSide(practice, countyId) {
    if (!practice) {
        return ""
    }
    if (countiesMatch(practice)) {
        return "patient"
    }
    const selected = Number(countyId || 0)
    if (selected && Number(practice.patient_county_id || 0) === selected) {
        return "patient"
    }
    if (selected && Number(practice.practice_county_id || 0) === selected) {
        return "practice"
    }
    return ""
}

export default function CreateLienFromDocument() {
    const dispatch = useDispatch()
    const userToken = useSelector((state) => state.user.token)
    const activeCase = useSelector((state) => state.case.activeCase)
    const permissionDisabled = useUserTabEditPermissionDisabled("lien_create_from_document")
    const doNotLienEngaged = Boolean(activeCase?.case?.do_not_lien)

    const [open, setOpen] = useState(false)
    const [step, setStep] = useState("upload")
    const [file, setFile] = useState(null)
    const [reading, setReading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [form, setForm] = useState(null)

    const practice = useMemo(() => {
        if (!form) {
            return null
        }
        return (form.practices || []).find((item) => String(item.id) === String(form.case_practice_id)) || null
    }, [form])

    const county = useMemo(() => {
        if (!form) {
            return null
        }
        return (form.counties || []).find((item) => String(item.id) === String(form.county_id)) || null
    }, [form])

    const sourceOption = useMemo(() => {
        if (!form?.source_lien_id) {
            return null
        }
        return (form.source_options || []).find((item) => String(item.lien_id) === String(form.source_lien_id) && item.side === form.side)
            || (form.source_options || []).find((item) => String(item.lien_id) === String(form.source_lien_id))
            || null
    }, [form])

    const sameCountyOriginal = form?.type === "lien" && countiesMatch(practice)
    const needsSource = form?.type === "amend" || form?.type === "satisfy"
    const showsAmount = form?.type === "lien" || form?.type === "amend"

    function closeModal() {
        setOpen(false)
        setStep("upload")
        setFile(null)
        setForm(null)
        setReading(false)
        setSaving(false)
    }

    function updateForm(patch) {
        setForm((current) => ({ ...current, ...patch }))
    }

    function onPracticeChange(casePracticeId) {
        const nextPractice = (form.practices || []).find((item) => String(item.id) === String(casePracticeId)) || null
        const patch = { case_practice_id: casePracticeId || "" }
        if (form.type === "lien") {
            patch.side = suggestOriginalSide(nextPractice, form.county_id)
        }
        updateForm(patch)
    }

    function onCountyChange(countyId) {
        const patch = { county_id: countyId || "" }
        if (form.type === "lien") {
            patch.side = suggestOriginalSide(practice, countyId)
        }
        updateForm(patch)
    }

    function onTypeChange(type) {
        const patch = { type }
        if (type === "lien") {
            patch.side = suggestOriginalSide(practice, form.county_id)
            patch.source_lien_id = ""
        } else {
            patch.side = ""
        }
        updateForm(patch)
    }

    function onSourceChange(key) {
        const option = (form.source_options || []).find((item) => item.key === key) || null
        if (!option) {
            updateForm({ source_lien_id: "", source_side: "" })
            return
        }
        updateForm({
            source_lien_id: option.lien_id,
            source_side: option.side,
            side: option.side,
            county_id: option.county_id || form.county_id,
        })
    }

    async function readDocument() {
        if (!file) {
            dispatch(notificationActions.setError({ errorMessage: "Choose a document first." }))
            return
        }
        const sizeError = validateBoxUploadFile(file)
        if (sizeError) {
            dispatch(notificationActions.setError({
                errorMessage: `"${file.name}" is too large. ${sizeError}`,
            }))
            return
        }

        const body = new FormData()
        body.append("file", file)
        setReading(true)
        try {
            const response = await parseLienDocument(userToken, activeCase.case.id, body)
            if (!response || response.errorMessage) {
                dispatch(notificationActions.setError({
                    errorMessage: errorText(response, "The document could not be read."),
                }))
                return
            }
            setForm({
                ...response,
                case_practice_id: response.case_practice_id || "",
                county_id: response.county_id || "",
                side: response.side || "",
                source_lien_id: response.source_lien_id || "",
                recorded_date: response.recorded_date || "",
                book: response.book || "",
                page: response.page || "",
                amount: response.amount || "",
            })
            setStep("review")
        } finally {
            setReading(false)
        }
    }

    function continueToConfirm() {
        if (!form?.case_practice_id) {
            dispatch(notificationActions.setError({ errorMessage: "Choose a practice." }))
            return
        }
        if (!form.county_id) {
            dispatch(notificationActions.setError({ errorMessage: "Choose the county." }))
            return
        }
        if (!form.side) {
            dispatch(notificationActions.setError({ errorMessage: "Choose patient or practice side." }))
            return
        }
        if (needsSource && !form.source_lien_id) {
            dispatch(notificationActions.setError({ errorMessage: "Choose the source lien." }))
            return
        }
        setStep("confirm")
    }

    async function createLien() {
        setSaving(true)
        try {
            const response = await createLienFromDocument(userToken, activeCase.case.id, {
                upload_token: form.upload_token,
                case_practice_id: Number(form.case_practice_id),
                type: form.type,
                side: sameCountyOriginal ? "patient" : form.side,
                county_id: Number(form.county_id),
                recorded_date: form.recorded_date || null,
                book: form.book || null,
                page: form.page || null,
                source_lien_id: needsSource ? Number(form.source_lien_id) : null,
                amount: form.type === "lien" || form.type === "amend" ? (form.amount || null) : null,
            }, dispatch)

            if (!response || response.errorMessage) {
                dispatch(notificationActions.setError({
                    errorMessage: errorText(response, "The lien could not be created."),
                }))
                return
            }

            dispatch(notificationActions.setError({
                errorMessage: "Lien created from the uploaded document.",
                type: "success",
            }))
            closeModal()
        } finally {
            setSaving(false)
        }
    }

    if (permissionDisabled || doNotLienEngaged) {
        return null
    }

    const statusLabel = form?.book && form?.page ? "Recorded" : "Received"
    const sideLabel = (sameCountyOriginal ? "patient" : form?.side) === "practice" ? "Practice" : (form?.side ? "Patient" : "")

    return <>
        <Button btnStyle="btn_small" click={() => setOpen(true)}>
            <i className="ti ti-upload"></i>
            Create from document
        </Button>

        {open && <>
            {ReactDOM.createPortal(
                <Backdrop hide={closeModal} />,
                document.getElementById("backdrop-root")
            )}
            {ReactDOM.createPortal(
                <Modal>
                    <ModalInnerWrapper closeModal={closeModal} modalTitle="Create lien from document" setWidth="560px">
                        {step === "upload" && <>
                            <PannelBody>
                                <div className={classes.form}>
                                    <p className={classes.file_name}>
                                        Upload the county document first. The system reads the chart number, county, date, book, and page. You can correct anything before it is saved.
                                    </p>
                                    <div className={classes.field}>
                                        <label htmlFor="lien-document-file">Document</label>
                                        <input
                                            id="lien-document-file"
                                            type="file"
                                            accept=".pdf,.tif,.tiff,.jpg,.jpeg,.png,application/pdf,image/tiff,image/jpeg,image/png"
                                            onChange={(event) => setFile(event.target.files?.[0] || null)}
                                        />
                                    </div>
                                    {file ? <p className={classes.file_name}>{file.name}</p> : null}
                                </div>
                            </PannelBody>
                            <PannelFooter>
                                <div className={classes.actions}>
                                    <Button btnStyle="btn_grey" click={closeModal}>Cancel</Button>
                                    <Button click={readDocument} isLoading={reading} isDisabled={!file}>Read document</Button>
                                </div>
                            </PannelFooter>
                        </>}

                        {step === "review" && form && <>
                            <PannelBody>
                                <div className={classes.form}>
                                    {form.file_name ? <p className={classes.file_name}>{form.file_name}</p> : null}
                                    {form.warnings?.length > 0 && (
                                        <ul className={classes.warnings}>
                                            {form.warnings.map((warning) => (
                                                <li key={warning}>{warning}</li>
                                            ))}
                                        </ul>
                                    )}
                                    <div className={classes.field}>
                                        <label htmlFor="lien-document-practice">Practice</label>
                                        <select
                                            id="lien-document-practice"
                                            value={form.case_practice_id || ""}
                                            onChange={(event) => onPracticeChange(event.target.value)}
                                        >
                                            <option value="">Choose a practice</option>
                                            {(form.practices || []).map((item) => (
                                                <option key={item.id} value={item.id}>
                                                    {item.name}{item.chart_number ? ` — ${item.chart_number}` : ""}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className={classes.field}>
                                        <label htmlFor="lien-document-type">Lien type</label>
                                        <select
                                            id="lien-document-type"
                                            value={form.type || "lien"}
                                            onChange={(event) => onTypeChange(event.target.value)}
                                        >
                                            <option value="lien">Lien</option>
                                            <option value="amend">Amended</option>
                                            <option value="satisfy">Satisfaction</option>
                                        </select>
                                    </div>
                                    <div className={classes.field}>
                                        <label htmlFor="lien-document-side">Side</label>
                                        <select
                                            id="lien-document-side"
                                            value={sameCountyOriginal ? "patient" : (form.side || "")}
                                            disabled={sameCountyOriginal}
                                            onChange={(event) => updateForm({ side: event.target.value })}
                                        >
                                            <option value="">Choose a side</option>
                                            <option value="patient">Patient</option>
                                            <option value="practice">Practice</option>
                                        </select>
                                    </div>
                                    <div className={classes.field}>
                                        <label htmlFor="lien-document-county">County</label>
                                        <select
                                            id="lien-document-county"
                                            value={form.county_id || ""}
                                            onChange={(event) => onCountyChange(event.target.value)}
                                        >
                                            <option value="">Choose a county</option>
                                            {(form.counties || []).map((item) => (
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className={classes.field}>
                                        <label htmlFor="lien-document-date">Recording date</label>
                                        <input
                                            id="lien-document-date"
                                            type="date"
                                            value={form.recorded_date || ""}
                                            onChange={(event) => updateForm({ recorded_date: event.target.value })}
                                        />
                                    </div>
                                    <div className={classes.field}>
                                        <label htmlFor="lien-document-book">Book</label>
                                        <input
                                            id="lien-document-book"
                                            value={form.book || ""}
                                            onChange={(event) => updateForm({ book: event.target.value })}
                                        />
                                    </div>
                                    <div className={classes.field}>
                                        <label htmlFor="lien-document-page">Page</label>
                                        <input
                                            id="lien-document-page"
                                            value={form.page || ""}
                                            onChange={(event) => updateForm({ page: event.target.value })}
                                        />
                                    </div>
                                    {showsAmount && (
                                        <div className={classes.field}>
                                            <label htmlFor="lien-document-amount">Amount</label>
                                            <input
                                                id="lien-document-amount"
                                                inputMode="decimal"
                                                value={form.amount || ""}
                                                onChange={(event) => updateForm({ amount: event.target.value })}
                                            />
                                        </div>
                                    )}
                                    {needsSource && (
                                        <div className={classes.field}>
                                            <label htmlFor="lien-document-source">Source lien</label>
                                            <select
                                                id="lien-document-source"
                                                value={sourceOption?.key || ""}
                                                onChange={(event) => onSourceChange(event.target.value)}
                                            >
                                                <option value="">Choose the source lien</option>
                                                {(form.source_options || []).map((item) => (
                                                    <option key={item.key} value={item.key}>{item.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </PannelBody>
                            <PannelFooter>
                                <div className={classes.actions}>
                                    <Button btnStyle="btn_grey" click={() => setStep("upload")}>Back</Button>
                                    <Button click={continueToConfirm}>Continue</Button>
                                </div>
                            </PannelFooter>
                        </>}

                        {step === "confirm" && form && <>
                            <PannelBody>
                                <dl className={classes.summary}>
                                    <dt>Practice</dt>
                                    <dd>{practice ? `${practice.name}${practice.chart_number ? ` — ${practice.chart_number}` : ""}` : "—"}</dd>
                                    <dt>Lien type</dt>
                                    <dd>{TYPE_LABELS[form.type] || form.type}</dd>
                                    <dt>Side</dt>
                                    <dd>{sideLabel || "—"}</dd>
                                    <dt>County</dt>
                                    <dd>{county?.name || "—"}</dd>
                                    <dt>Recording date</dt>
                                    <dd>{form.recorded_date || "—"}</dd>
                                    <dt>Book</dt>
                                    <dd>{form.book || "—"}</dd>
                                    <dt>Page</dt>
                                    <dd>{form.page || "—"}</dd>
                                    <dt>Status</dt>
                                    <dd>{statusLabel}</dd>
                                    {showsAmount && <>
                                        <dt>Amount</dt>
                                        <dd>{form.amount || "—"}</dd>
                                    </>}
                                    {needsSource && <>
                                        <dt>Source lien</dt>
                                        <dd>{sourceOption?.label || "—"}</dd>
                                    </>}
                                </dl>
                            </PannelBody>
                            <PannelFooter>
                                <div className={classes.actions}>
                                    <Button btnStyle="btn_grey" click={() => setStep("review")}>Back</Button>
                                    <Button click={createLien} isLoading={saving}>Create lien</Button>
                                </div>
                            </PannelFooter>
                        </>}
                    </ModalInnerWrapper>
                </Modal>,
                document.getElementById("overlay-root")
            )}
        </>}
    </>
}
